import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { useGoogleIdentityInit } from "@/hooks/useGoogleIdentityInit";
import { isInCooldown, recordDismissal } from "@/lib/gsiCooldown";

interface GoogleOneTapOptions {
  /** Whether to enable the One Tap prompt */
  enabled?: boolean;
  /** Auto-select credential if only one available */
  autoSelect?: boolean;
  /** Cancel prompt when user taps outside */
  cancelOnTapOutside?: boolean;
  /** Context for the prompt: "signin" (login page), "signup" (register), "use" (general app) */
  context?: "signin" | "signup" | "use";
}

export function useGoogleOneTap(options: GoogleOneTapOptions = {}) {
  const {
    enabled = true,
    autoSelect = true,
    cancelOnTapOutside = true,
    context = "signin",
  } = options;

  const { isAuthenticated } = useAuth();
  const promptAttempted = useRef(false);
  const { initialize } = useGoogleIdentityInit({ context, autoSelect, cancelOnTapOutside });

  useEffect(() => {
    if (!enabled || isAuthenticated || promptAttempted.current) {
      return;
    }

    if (isInCooldown()) {
      return;
    }

    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!clientId) {
      return;
    }

    const showPrompt = () => {
      if (!window.google?.accounts?.id) return;
      if (!initialize()) return;

      promptAttempted.current = true;

      // With FedCM the browser handles the prompt UI natively and the legacy
      // notification callback is deprecated. Since we can't detect dismissal,
      // record it immediately after showing so the cooldown persists across
      // page reloads (minimum 2 h before re-appearing).
      // resetCooldown() in useGoogleIdentityInit clears this on successful auth.
      window.google.accounts.id.prompt();
      recordDismissal();
    };

    if (window.google?.accounts?.id) {
      showPrompt();
    } else {
      const checkInterval = setInterval(() => {
        if (window.google?.accounts?.id) {
          clearInterval(checkInterval);
          showPrompt();
        }
      }, 100);

      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
      }, 5000);

      return () => {
        clearInterval(checkInterval);
        clearTimeout(timeout);
      };
    }
  }, [enabled, isAuthenticated, initialize]);

  // Expose recordDismissal for callers that track dismissal via other signals
  return { recordDismissal };
}
